import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { quizAttemptService } from '../services/learning/QuizAttemptService';
import { API_CONFIG } from '../config/api.config';

const QUIZ_EXPIRATION_MINUTES = 20;

// Função helper para verificar se tentativa expirou
const isAttemptExpired = (startedAt: string): boolean => {
  const startTime = new Date(startedAt).getTime();
  const currentTime = Date.now();
  const diffMinutes = (currentTime - startTime) / (1000 * 60);
  return diffMinutes > QUIZ_EXPIRATION_MINUTES;
};

interface UseQuizNavigationBlockerOptions {
  isBlocked: boolean;
  onNavigationAttempt: () => void;
  startedAt?: string;
  attemptId?: string;
}

/**
 * Hook para bloquear navegação durante um quiz em andamento
 * Intercepta:
 * - Botão voltar do navegador (popstate)
 * - Tentativas de navegação programática (através de um wrapper do navigate)
 * - Cliques em links (através de event delegation)
 */
export const useQuizNavigationBlocker = ({
  isBlocked,
  onNavigationAttempt,
  startedAt,
  attemptId,
}: UseQuizNavigationBlockerOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigateRef = useRef(navigate);
  const isBlockedRef = useRef(isBlocked);
  const startedAtRef = useRef(startedAt);
  const attemptIdRef = useRef(attemptId);

  // Atualizar refs
  useEffect(() => {
    navigateRef.current = navigate;
    isBlockedRef.current = isBlocked;
    startedAtRef.current = startedAt;
    attemptIdRef.current = attemptId;
  }, [navigate, isBlocked, startedAt, attemptId]);

  // Verificar se deve bloquear (considerando expiração)
  const shouldBlock = () => {
    if (!isBlockedRef.current) return false;
    if (startedAtRef.current && isAttemptExpired(startedAtRef.current)) {
      return false; // Não bloquear se expirada
    }
    return true;
  };

  useEffect(() => {
    if (!shouldBlock()) return;

    // Interceptar botão voltar do navegador
    const handlePopState = (e: PopStateEvent) => {
      if (shouldBlock()) {
        e.preventDefault();
        onNavigationAttempt();
        // Restaurar o estado anterior
        window.history.pushState(null, '', location.pathname);
      }
    };

    // Adicionar estado ao histórico para interceptar botão voltar
    window.history.pushState(null, '', location.pathname);

    window.addEventListener('popstate', handlePopState);

    // Interceptar cliques em links
    const handleLinkClick = (e: MouseEvent) => {
      if (!shouldBlock()) return;

      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;

      if (link && link.href) {
        // Verificar se é um link interno (mesmo origin)
        try {
          const linkUrl = new URL(link.href);
          const currentUrl = new URL(window.location.href);

          // Se for link interno e não for a mesma página
          if (
            linkUrl.origin === currentUrl.origin &&
            linkUrl.pathname !== currentUrl.pathname
          ) {
            e.preventDefault();
            e.stopPropagation();
            onNavigationAttempt();
          }
        } catch (err) {
          // Se não conseguir parsear a URL, ignorar
        }
      }
    };

    // Usar capture phase para interceptar antes que o link seja processado
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [isBlocked, location.pathname, onNavigationAttempt]);

  // Avisar ao fechar aba/navegador e tentar marcar como abandonada
  useEffect(() => {
    if (!shouldBlock()) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Tentar marcar como abandonada usando Beacon API
      // Nota: Beacon API não suporta headers customizados, então a autenticação pode falhar
      // Mas tentamos enviar mesmo assim. O backend já verifica expiração automaticamente.
      if (attemptIdRef.current && startedAtRef.current && !isAttemptExpired(startedAtRef.current)) {
        try {
          const baseUrl = `${API_CONFIG.baseURL}/quiz-attempts/${attemptIdRef.current}/abandon`;
          
          if (navigator.sendBeacon) {
            // Beacon API não suporta headers, então a autenticação falhará
            // Mas tentamos enviar mesmo assim - o backend pode ter lógica alternativa
            const blob = new Blob([JSON.stringify({})], { type: 'application/json' });
            navigator.sendBeacon(baseUrl, blob);
          }
        } catch (err) {
          // Silenciosamente ignorar - não é crítico, backend já verifica expiração
        }
      }
      
      e.preventDefault();
      e.returnValue = ''; // Chrome requer returnValue
      return ''; // Alguns navegadores
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isBlocked, startedAt, attemptId]);
};

